import { useState, useEffect } from 'react';
import {
  Search,
  User,
  MapPin,
  Phone,
  Trophy,
  ShoppingBag,
  ChevronLeft,
  ChevronRight,
  X,
  CreditCard,
  Calendar,
  ArrowLeftRight,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';

interface CustomerSummary {
  totalSpend: number;
  visitCount: number;
  loyaltyBalance: number;
}

interface InvoiceItem {
  id: string;
  productName: string;
  hsnCode: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  discount: number;
  gstRate: number;
  totalAmount: number;
}

interface InvoiceHistory {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  grandTotal: number;
  subtotal: number;
  paymentMode: string;
  status: string;
  items: InvoiceItem[];
}

interface Customer {
  id: string;
  name: string;
  mobileNumber: string;
  cityOrPlace: string | null;
  customerGstin: string | null;
  currentLoyaltyBalance: number;
  createdAt: string;
  _count?: {
    invoices: number;
  };
}

interface Customer360 extends Customer {
  invoices: InvoiceHistory[];
  summary: CustomerSummary;
}

export function CustomerDirectory() {
  const { user } = useAuth();
  const tenantId = user?.tenantId;

  // List states
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Drilldown states
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [customerDetails, setCustomerDetails] = useState<Customer360 | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<string | null>(null);

  // Fetch customer list
  const fetchCustomers = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const response = await api.get('/tenant/customers', {
        params: { page, limit: 12, search },
        headers: { 'x-tenant-id': tenantId },
      });
      if (response.data.success) {
        setCustomers(response.data.customers);
        setPage(response.data.pagination.page);
        setTotalPages(response.data.pagination.totalPages);
        setTotalCount(response.data.pagination.total);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch 360 view details
  const fetchCustomerDetails = async (id: string) => {
    if (!tenantId) return;
    setLoadingDetails(true);
    setExpandedInvoiceId(null);
    try {
      const response = await api.get(`/tenant/customers/${id}/purchase-history`, {
        headers: { 'x-tenant-id': tenantId },
      });
      if (response.data.success) {
        setCustomerDetails(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching customer history:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Trigger search on parameter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchCustomers();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, tenantId]);

  useEffect(() => {
    fetchCustomers();
  }, [page, tenantId]);

  useEffect(() => {
    if (selectedCustomerId) {
      fetchCustomerDetails(selectedCustomerId);
    } else {
      setCustomerDetails(null);
    }
  }, [selectedCustomerId]);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-200 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <User className="text-emerald-600" size={24} />
            Customer CRM & Loyalty Engine
          </h1>
          <p className="mt-1 text-sm text-slate-500 font-light">
            Monitor client metrics, drill down into 360° transaction timelines, and trace point
            rewards.
          </p>
        </div>
        <div className="relative w-72">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name, phone or city..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="block w-full pl-9 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-800 focus:outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 transition-all shadow-sm"
          />
        </div>
      </div>

      {/* Main Grid View */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 py-8">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="animate-pulse bg-slate-50 border border-slate-200/60 rounded-xl p-4 h-40 flex flex-col justify-between"
            >
              <div className="space-y-2">
                <div className="h-4 bg-slate-200 rounded w-2/3"></div>
                <div className="h-3 bg-slate-200 rounded w-1/2"></div>
              </div>
              <div className="h-6 bg-slate-200 rounded w-1/3"></div>
            </div>
          ))}
        </div>
      ) : customers.length === 0 ? (
        <div className="text-center py-20 bg-slate-50 rounded-2xl border border-dashed border-slate-200 max-w-lg mx-auto">
          <User size={48} className="mx-auto mb-3 opacity-30 text-emerald-600" />
          <p className="text-sm font-bold text-slate-700">No customers found.</p>
          <p className="text-xs text-slate-400 mt-1">
            Once a customer checkout is finished, their profile will appear here dynamically.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {customers.map((c) => (
              <div
                key={c.id}
                onClick={() => setSelectedCustomerId(c.id)}
                className="bg-white border border-slate-200 hover:border-emerald-500 rounded-xl p-4 shadow-sm hover:shadow-md cursor-pointer transition-all duration-200 flex flex-col justify-between group active:scale-[0.99]"
              >
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-bold text-slate-800 text-sm line-clamp-1 group-hover:text-emerald-700 transition-colors">
                      {c.name}
                    </h3>
                    <span className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-700 font-bold rounded-full flex items-center gap-0.5">
                      <Trophy size={9} />
                      {c.currentLoyaltyBalance} Pts
                    </span>
                  </div>
                  <div className="mt-3 space-y-1.5 text-xs text-slate-550">
                    <div className="flex items-center gap-1.5">
                      <Phone size={12} className="text-slate-400" />
                      <span className="font-mono">{c.mobileNumber}</span>
                    </div>
                    {c.cityOrPlace && (
                      <div className="flex items-center gap-1.5">
                        <MapPin size={12} className="text-slate-400" />
                        <span>{c.cityOrPlace}</span>
                      </div>
                    )}
                    {c.customerGstin && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[9px] font-extrabold bg-slate-100 text-slate-500 px-1 py-0.5 rounded">
                          GST
                        </span>
                        <span className="font-mono text-[10px]">{c.customerGstin}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-3 mt-4 flex items-center justify-between text-[11px] text-slate-400">
                  <span>Joined {new Date(c.createdAt).toLocaleDateString()}</span>
                  <span className="font-semibold text-slate-600 bg-slate-50 px-2 py-0.5 rounded border border-slate-100">
                    {c._count?.invoices ?? 0} bills
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-100 pt-4 mt-6">
              <span className="text-xs text-slate-450">
                Showing page <span className="font-semibold text-slate-700">{page}</span> of{' '}
                <span className="font-semibold text-slate-700">{totalPages}</span> ({totalCount}{' '}
                total)
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="p-1.5 border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-50 cursor-pointer text-slate-650"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="p-1.5 border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-50 cursor-pointer text-slate-650"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Drill-down Modal (360° History Profile) */}
      {selectedCustomerId && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden animate-slideUp">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <User size={18} className="text-emerald-600" />
                  <span>Customer 360° Timeline Profile</span>
                </h2>
                <p className="text-xs text-slate-450 mt-0.5">
                  Unified CRM insights and historical purchases ledger.
                </p>
              </div>
              <button
                onClick={() => setSelectedCustomerId(null)}
                className="p-1 text-slate-400 hover:text-slate-650 hover:bg-slate-200/50 rounded-lg transition-all cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {loadingDetails ? (
                <div className="flex flex-col items-center justify-center py-20 text-slate-450 gap-2">
                  <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
                  <p className="text-xs font-semibold">Consolidating profile metrics...</p>
                </div>
              ) : customerDetails ? (
                <>
                  {/* KPI Cards Header */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center gap-3">
                      <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-lg">
                        <ShoppingBag size={20} />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                          Total Sales Spend
                        </p>
                        <p className="text-lg font-black text-slate-800 font-mono">
                          ₹{customerDetails.summary.totalSpend.toFixed(2)}
                        </p>
                      </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center gap-3">
                      <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg">
                        <ArrowLeftRight size={20} />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                          Total Store Visits
                        </p>
                        <p className="text-lg font-black text-slate-800 font-mono">
                          {customerDetails.summary.visitCount} visits
                        </p>
                      </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center gap-3">
                      <div className="p-2.5 bg-amber-50 text-amber-600 rounded-lg">
                        <Trophy size={20} />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                          Loyalty Balance
                        </p>
                        <p className="text-lg font-black text-slate-800 font-mono">
                          {customerDetails.summary.loyaltyBalance} pts
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Customer Information Metadata Grid */}
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-slate-400 font-semibold mb-1">Full Client Name</p>
                      <p className="font-bold text-slate-800">{customerDetails.name}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 font-semibold mb-1">Mobile Phone Number</p>
                      <p className="font-bold font-mono text-slate-800">
                        {customerDetails.mobileNumber}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-400 font-semibold mb-1">City or Place</p>
                      <p className="font-bold text-slate-800">
                        {customerDetails.cityOrPlace || 'Not Specified'}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-400 font-semibold mb-1">Tax ID / GSTIN</p>
                      <p className="font-bold font-mono text-slate-800">
                        {customerDetails.customerGstin || 'None'}
                      </p>
                    </div>
                  </div>

                  {/* Purchase History Invoice List */}
                  <div>
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
                      Historical Bills & Invoices ({customerDetails.invoices.length})
                    </h3>
                    {customerDetails.invoices.length === 0 ? (
                      <div className="text-center py-10 bg-slate-50 rounded-xl border border-slate-200 text-slate-400 text-xs">
                        No purchase transactions associated with this customer profile yet.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {customerDetails.invoices.map((inv) => (
                          <div
                            key={inv.id}
                            className="border border-slate-200 rounded-xl bg-white overflow-hidden shadow-sm"
                          >
                            {/* Accordion Trigger Header */}
                            <div
                              onClick={() =>
                                setExpandedInvoiceId(expandedInvoiceId === inv.id ? null : inv.id)
                              }
                              className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-slate-50/50 cursor-pointer transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <span className="font-bold font-mono text-xs text-slate-800">
                                  {inv.invoiceNumber}
                                </span>
                                <span
                                  className={`text-[9px] px-1.5 py-0.5 rounded font-extrabold ${
                                    inv.status === 'PAID'
                                      ? 'bg-emerald-55 text-emerald-800'
                                      : 'bg-amber-55 text-amber-800'
                                  }`}
                                >
                                  {inv.status}
                                </span>
                              </div>

                              <div className="flex items-center gap-4 text-xs text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Calendar size={13} />
                                  {new Date(inv.invoiceDate).toLocaleDateString()}
                                </span>
                                <span className="flex items-center gap-1">
                                  <CreditCard size={13} />
                                  {inv.paymentMode}
                                </span>
                                <span className="font-bold text-slate-900 font-mono">
                                  ₹{inv.grandTotal.toFixed(2)}
                                </span>
                              </div>
                            </div>

                            {/* Accordion Body: Items list */}
                            {expandedInvoiceId === inv.id && (
                              <div className="border-t border-slate-100 bg-slate-50/30 p-4">
                                <table className="w-full text-left text-[11px] text-slate-600">
                                  <thead>
                                    <tr className="border-b border-slate-200 text-slate-400 font-bold uppercase pb-2">
                                      <th className="pb-2 text-center w-12">S.No.</th>
                                      <th className="pb-2">Product Details</th>
                                      <th className="pb-2 text-right">Price</th>
                                      <th className="pb-2 text-center">Qty</th>
                                      <th className="pb-2 text-center">Disc (%)</th>
                                      <th className="pb-2 text-center">GST Rate</th>
                                      <th className="pb-2 text-right">Total Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100">
                                    {inv.items.map((item, itemIdx) => (
                                      <tr key={item.id}>
                                        <td className="py-2 text-center text-slate-500 font-medium">{itemIdx + 1}</td>
                                        <td className="py-2">
                                          <span className="font-bold text-slate-800">
                                            {item.productName}
                                          </span>
                                          <span className="block text-[9px] text-slate-400 font-mono">
                                            HSN: {item.hsnCode}
                                          </span>
                                        </td>
                                        <td className="py-2 text-right font-mono">
                                          ₹{item.unitPrice.toFixed(2)}
                                        </td>
                                        <td className="py-2 text-center font-bold">
                                          {item.quantity} {item.unit}
                                        </td>
                                        <td className="py-2 text-center font-mono">
                                          {item.discount}%
                                        </td>
                                        <td className="py-2 text-center font-mono">
                                          {item.gstRate}%
                                        </td>
                                        <td className="py-2 text-right font-mono font-bold text-slate-800">
                                          ₹{item.totalAmount.toFixed(2)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-20 text-rose-500 text-xs">
                  Failed to retrieve customer timeline history.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
